import { TopBarMessageResponseTypeEnum, TopBarMessageResponse } from '@interfaces/models';
import { useHasPermission, useCurrentUser } from '@shared/hooks';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Routes } from '@shared/enums';

import { TopbarMessageConfiguration } from './use-topbar-configuration';

const MESSAGE_CONFIG_MAP: Record<
  TopBarMessageResponseTypeEnum,
  {
    ctaVariant: TopbarMessageConfiguration['ctaVariant'];
    variant: TopbarMessageConfiguration['variant'];
    titleKey: string;
    ctaKey: string;
  }
> = {
  [TopBarMessageResponseTypeEnum.SINGLE_BALANCE_LOW]: {
    titleKey: 'TOPBAR.SINGLE_BALANCE_LOW_TITLE',
    ctaKey: 'TOPBAR.SINGLE_BALANCE_LOW_CTA',
    ctaVariant: 'primary',
    variant: 'warning',
  },
  [TopBarMessageResponseTypeEnum.SINGLE_OUT_OF_BALANCE]: {
    titleKey: 'TOPBAR.SINGLE_OUT_OF_BALANCE_TITLE',
    ctaKey: 'TOPBAR.SINGLE_OUT_OF_BALANCE_CTA',
    ctaVariant: 'secondary',
    variant: 'critical',
  },
  [TopBarMessageResponseTypeEnum.SOME_BALANCE_LOW]: {
    titleKey: 'TOPBAR.SOME_BALANCE_LOW_TITLE',
    ctaKey: 'TOPBAR.SOME_BALANCE_LOW_CTA',
    ctaVariant: 'primary',
    variant: 'warning',
  },
  [TopBarMessageResponseTypeEnum.SOME_OUT_OF_BALANCE]: {
    titleKey: 'TOPBAR.SOME_OUT_OF_BALANCE_TITLE',
    ctaKey: 'TOPBAR.SOME_OUT_OF_BALANCE_CTA',
    ctaVariant: 'secondary',
    variant: 'critical',
  },
  [TopBarMessageResponseTypeEnum.ALL_OUT_OF_BALANCE]: {
    titleKey: 'TOPBAR.ALL_OUT_OF_BALANCE_TITLE',
    ctaKey: 'TOPBAR.ALL_OUT_OF_BALANCE_CTA',
    ctaVariant: 'secondary',
    variant: 'critical',
  },
  [TopBarMessageResponseTypeEnum.SINGLE_ACCOUNT_NEVER_TOP_UP]: {
    titleKey: 'TOPBAR.SINGLE_ACCOUNT_NEVER_TOP_UP_TITLE',
    ctaKey: 'TOPBAR.SINGLE_ACCOUNT_NEVER_TOP_UP_CTA',
    ctaVariant: 'secondary',
    variant: 'info',
  },
  [TopBarMessageResponseTypeEnum.MULTIPLE_ACCOUNT_NEVER_TOP_UP]: {
    titleKey: 'TOPBAR.MULTIPLE_ACCOUNT_NEVER_TOP_UP_TITLE',
    ctaKey: 'TOPBAR.MULTIPLE_ACCOUNT_NEVER_TOP_UP_CTA',
    ctaVariant: 'secondary',
    variant: 'info',
  },
};

export const useAccountMessage = (
  data: TopBarMessageResponse | undefined,
): TopbarMessageConfiguration | null => {
  const { t } = useTranslation();
  const { companyId } = useCurrentUser();
  const navigate = useNavigate();
  const hasTopUpPermission = useHasPermission('DASHBOARD_ACCOUNT_TOP_UP', 'WRITE');

  const topUpAction = () =>
    navigate(`/${companyId}/${Routes.DASHBOARD}`, {
      state: { accountDialogVisible: true },
    });

  if (!data?.type || !hasTopUpPermission) return null;

  const config = MESSAGE_CONFIG_MAP[data.type];
  if (!config) return null;

  const accountNumber = data.accountsNumbers?.join(', ');

  return {
    title: t(config.titleKey, { accountNumber }),
    ctaLabel: t(config.ctaKey),
    ctaVariant: config.ctaVariant,
    variant: config.variant,
    ctaAction: topUpAction,
  };
};
